import {useCallback, useMemo, useRef, useState} from 'react';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import {useAppDispatch, useAppSelector} from '@/store';
import {
  resetPerfectStore,
  setPerfectStoreFormData,
  setPerfectStoreMeta
} from '@/store/slices/perfectStoreSlice';
import {setToastNotifications} from '@/store/slices/commonSlice';
import {ROUTES} from '@/utils/constants/routesConstants';
import {
  buildPerfectStoreDuplicateFormData,
  buildPerfectStoreDuplicateMeta,
  downloadPerfectStoreReport,
  isPerfectStoreCompletedOrExpired,
  isPerfectStoreDownloadEnabled,
  isPerfectStoreDuplicateEnabled,
  isPerfectStoreEditable,
  isPerfectStoreFooterHiddenByDefault,
  isPerfectStoreSaveDraftEnabled,
  isPerfectStoreUpdateStoreVisitsTabEnabled
} from '@/helpers/perfectStoreUtils';
import {usePerfectStore} from '@/hooks/usePerfectStore';
import {usePerfectStoreQuery} from '@/hooks/usePerfectStoreQuery';
import {useAzureBlob} from '@/hooks/useAzureBlob-hook';
import {logger} from '@/helpers/logger';

export const usePerfectStorePreviewActions = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const {id: paramId} = useParams();
  const location = useLocation();
  const perfectStore = useAppSelector((state) => state?.perfectStore);
  const formData = useMemo(
    () => perfectStore?.formData || {},
    [perfectStore?.formData]
  );
  const status = perfectStore?.status || formData?.status;
  const {handleDraft, handlePublish} = usePerfectStore();
  const {downloadFromBlobUrl} = useAzureBlob();
  // enableQuery=false: only used for its refetch() to reload the list after publish
  const {refetch: refetchPerfectStoreList} = usePerfectStoreQuery(false);

  const targetId = paramId ?? perfectStore?.id;

  // Held in a ref, not state: passing a function to a state setter would be
  // interpreted as an updater and invoke it instead of storing it.
  const refetchVisitsRef = useRef(null);
  const setRefetchVisits = useCallback((refetch) => {
    refetchVisitsRef.current = refetch;
  }, []);

  const [isEditMode, setIsEditMode] = useState(
    () =>
      location?.state?.isEdit === true && location?.state?.fromWizard === true
  );
  const [isCancel, setIsCancel] = useState(false);
  // Update Store Visits tab state
  const [isPublishingUpdate, setIsPublishingUpdate] = useState(false);

  const isEditable = useMemo(() => isPerfectStoreEditable(status), [status]);
  const disableDraft = useMemo(
    () => !isPerfectStoreSaveDraftEnabled(status),
    [status]
  );
  const isDuplicateEnabled = useMemo(
    () => isPerfectStoreDuplicateEnabled(status),
    [status]
  );
  const isDownloadEnabled = useMemo(
    () => isPerfectStoreDownloadEnabled(status),
    [status]
  );
  const isCompletedOrExpired = useMemo(
    () => isPerfectStoreCompletedOrExpired(status),
    [status]
  );
  const isUpdateStoreVisitsTabEnabled = useMemo(
    () => isPerfectStoreUpdateStoreVisitsTabEnabled(status),
    [status]
  );
  const showFooter = useMemo(() => {
    if (isCompletedOrExpired) return false;
    if (isEditMode) return true;
    return !isPerfectStoreFooterHiddenByDefault(status);
  }, [isCompletedOrExpired, isEditMode, status]);

  const showProgressIndicator = useMemo(
    () => isEditMode && isEditable && !isCompletedOrExpired,
    [isEditMode, isEditable, isCompletedOrExpired]
  );

  const getNavigationState = useCallback(
    (returnTo) => ({
      isEdit: true,
      disableDraft,
      fromWizard: true,
      ...(location.pathname ? {from: location.pathname} : {}),
      ...(returnTo && {returnTo})
    }),
    [disableDraft, location.pathname]
  );

  const handleEditSection = useCallback(
    (step) => {
      if (!isEditable) return;
      setIsEditMode(true);
      const stepRoutes = {
        1: ROUTES.PERFECT_STORE_ADD,
        2: ROUTES.PERFECT_STORE_DISPLAY
      };
      if (stepRoutes[step]) {
        navigate(stepRoutes[step], {
          state: getNavigationState(step === 2 ? 'review' : undefined)
        });
      }
    },
    [getNavigationState, isEditable, navigate]
  );

  const handleDuplicate = useCallback(() => {
    if (!isDuplicateEnabled) return;
    const duplicatedFormData = buildPerfectStoreDuplicateFormData(formData);
    dispatch(resetPerfectStore());
    dispatch(setPerfectStoreFormData(duplicatedFormData));
    dispatch(setPerfectStoreMeta(buildPerfectStoreDuplicateMeta()));
    navigate(ROUTES.PERFECT_STORE_ADD, {
      state: {fromWizard: true, isDuplicate: true, isEdit: false}
    });
  }, [dispatch, formData, isDuplicateEnabled, navigate]);

  const handleDownload = useCallback(async () => {
    if (!isDownloadEnabled || !targetId) return;
    await downloadPerfectStoreReport(targetId, {
      downloadFromBlobUrl,
      targetName: formData?.name,
      onToast: (message, type) =>
        dispatch(setToastNotifications({show: true, message, type}))
    });
  }, [
    dispatch,
    downloadFromBlobUrl,
    formData?.name,
    isDownloadEnabled,
    targetId
  ]);

  const handleCancel = useCallback(() => {
    setIsCancel((prev) => !prev);
  }, []);

  const onConfirmCancel = useCallback(() => {
    dispatch(resetPerfectStore());
    navigate(ROUTES.PERFECT_STORE);
  }, [dispatch, navigate]);

  const handleDraftAction = useCallback(async () => {
    if (disableDraft) return;
    try {
      const response = await handleDraft(formData);
      if (response?.success) {
        navigate(ROUTES.PERFECT_STORE);
      }
    } catch (error) {
      logger.error('Error saving Perfect Store draft from preview:', error);
    }
  }, [disableDraft, formData, handleDraft, navigate]);

  const handlePublishAction = useCallback(async () => {
    try {
      const response = await handlePublish(formData);
      if (response?.success) {
        navigate(ROUTES.PERFECT_STORE);
      }
    } catch (error) {
      logger.error('Error publishing Perfect Store from preview:', error);
    }
  }, [formData, handlePublish, navigate]);

  // Update Store Visits tab: Publish updated visit-status CSV using regular publish API.
  const handleUpdateStoreVisitsPublish = useCallback(async () => {
    if (!targetId || isPublishingUpdate) return;
    setIsPublishingUpdate(true);
    try {
      const response = await handlePublish(formData);
      if (response?.success) {
        dispatch(
          setToastNotifications({
            show: true,
            message: 'Target Update Successfully Published',
            type: 'success'
          })
        );
        // Reload the visits list instead of navigating away
        refetchVisitsRef.current?.();
        refetchPerfectStoreList();
      } else {
        dispatch(
          setToastNotifications({
            show: true,
            message:
              response?.message ||
              'Failed to publish update. Please try again.',
            type: 'error'
          })
        );
      }
    } catch (error) {
      logger.error('Error publishing Update Store Visits:', error);
      dispatch(
        setToastNotifications({
          show: true,
          message: 'Failed to publish update. Please try again.',
          type: 'error'
        })
      );
    } finally {
      setIsPublishingUpdate(false);
    }
  }, [
    targetId,
    isPublishingUpdate,
    formData,
    handlePublish,
    dispatch,
    refetchPerfectStoreList
  ]);

  return {
    formData,
    status,
    isEditMode,
    setIsEditMode,
    isEditable,
    disableDraft,
    isDuplicateEnabled,
    isDownloadEnabled,
    isCompletedOrExpired,
    showFooter,
    showProgressIndicator,
    isCancel,
    handleEditSection,
    handleDuplicate,
    handleDownload,
    handleCancel,
    onConfirmCancel,
    handleDraftAction,
    handlePublishAction,
    getNavigationState,
    // Update Store Visits tab
    targetId,
    isUpdateStoreVisitsTabEnabled,
    isPublishingUpdate,
    handleUpdateStoreVisitsPublish,
    setRefetchVisits
  };
};

export default usePerfectStorePreviewActions;
